import React, { useState } from 'react';
import Table, {
  columnType,
  tempFilteredRows,
} from '../../../components/ClearingTable/Table';
import moment from 'moment-timezone';
import { formatPbDate, formatCurrency } from '../../../lib/fmt';
import Button from '../../../components/Buttons/Button';
import DateTimePicker from '../../../components/Textfields/DateTimePicker';
import { Visibility as ViewIcon } from '@material-ui/icons';
import AutoCompleteCorrespondent from '../../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteAccountName from '../../../components/AutoComplete/AutoCompleteAccountName';
import AutoCompleteMasterAccountNo from '../../../components/AutoComplete/AutoCompleteMasterAccountNo';
import SelectMultiple from '../../../components/MultipleSelect/SelectMultiple';
import { Checkbox as MUICheckbox, FormControlLabel, IconButton } from '@material-ui/core';
import Text from '../../../components/Typography/Text';
import {
  notifyInfo,
  notifyError,
  notifyWarning,
} from '../../../components/Messages/Notification';
import { listRequirement } from '../../../services/MarginRequirementService';
import { getPreviousDate } from '../../../services/ProfileService';
import QueryParam from '../../../services/QueryParamService';
import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderDate,
} from '../../../components/Table/CustomBodyRender';
import MarginRequirementDetails from './Components/MarginRequirementDetails';

export default function MarginRequirement() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [rowData, setRowData] = React.useState({});

  const [pagination, setPagination] = useState({});
  const [footerData, setFooterData] = useState({});
  const [totalRows, setTotalRows] = useState(0);

  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      tradeDate: '',
      correspondent: '',
      masterAccountNo: '',
      accountName: '',
      fieldsNotZero: [],
    })
  );

  const init = async () => {
    const searchParameters = localStorage.getItem('search_data');

    const { tradeDate, correspondent, masterAccountNo, accountName } = searchParameters
      ? JSON.parse(searchParameters)
      : searchData;

    const systemDate = await getPreviousDate();

    setSearchData({
      ...searchData,
      tradeDate: tradeDate ? tradeDate : systemDate,
      correspondent: correspondent ? correspondent : '',
      masterAccountNo: masterAccountNo ? masterAccountNo : '',
      accountName: accountName ? accountName : '',
    });
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line
  }, []);

  const handleOpen = (tradeDate, masterAccountNo) => {
    setRowData({ tradeDate: tradeDate, masterAccountNo: masterAccountNo });
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const columns = [
    {
      name: 'action',
      label: 'Action',
      type: columnType.buttons,
      sort: false,
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <div className="grd-row">
              <div className="grd-cell nm">
                <IconButton aria-label="view">
                  <ViewIcon
                    style={{ height: 24, width: 24, marginTop: -20 }}
                    onClick={() => {
                      const tradeDate = moment(
                        new Date(formatPbDate(tempFilteredRows[dataIndex]?.tradeDate))
                      ).format('yyyy-MM-DD');

                      handleOpen(tradeDate, tempFilteredRows[dataIndex]?.masterAccountNo);
                    }}
                  />
                </IconButton>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'requirementId',
      label: 'Requirement ID',
      options: {
        display: false,
      },
    },
    {
      name: 'tradeDate',
      label: 'Trade Date',
      type: columnType.date,
      options: {
        customBodyRender: CustomBodyRenderDate,
      },
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
    },
    {
      name: 'accountName',
      label: 'Account Name',
    },
    {
      name: 'cashBalance',
      label: 'Cash Balance',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'longMarketValue',
      label: 'Long Market Value',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'shortMarketValue',
      label: 'Short Market Value',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'totalEquity',
      label: 'Total Equity',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'exchangeReq',
      label: 'Exchange Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'exchangeCall',
      label: 'Exchange Call',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'fedReq',
      label: 'Fed Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'fedCall',
      label: 'Fed Call',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'houseReq',
      label: 'House Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'houseCall',
      label: 'House Call',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'pmHouseReq',
      label: 'PM House Req',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'pmHouseCall',
      label: 'PM House Call',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'adjFedReq',
      label: 'Adjusted Fed Req',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'adjExchangeReq',
      label: 'Adjusted Exchange Req',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'adjHouseReq',
      label: 'Adjusted House Req',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'longOptionMarketValue',
      label: 'Long Option Market Value',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'shortOptionMarketValue',
      label: 'Short Option Market Value',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'timsReq',
      label: 'Tims Req',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'timsCall',
      label: 'Tims Call',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'buyingPowerCall',
      label: 'Buying Power Call',
      type: columnType.amount,
    },
    {
      name: 'minBalanceCall',
      label: 'Min Balance Call',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'houseExcess',
      label: 'House Excess',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'marginEquity',
      label: 'Margin Equity',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'fedExcess',
      label: 'Fed Excess',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'exchangeExcess',
      label: 'Exchange Excess',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'minBalanceExcess',
      label: 'Min Balance Excess',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'pmHouseExcess',
      label: 'PM House Excess',
      type: columnType.amount,
      addFooter: true,
    },
    {
      name: 'timsExcess',
      label: 'Tims Excess',
      type: columnType.amount,
      addFooter: true,
    },
    {
      name: 'minBalanceReq',
      label: 'Min Balance Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'totalAdjustmentFedReq',
      label: 'Total Adjustment Fed Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'totalAdjustmentHouseReq',
      label: 'Total Adjustment House Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'totalAdjustmentExchangeReq',
      label: 'Total Adjustment Exchange Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'cashCallReq',
      label: 'Cash Call Req',
      type: columnType.amount,
    },
    {
      name: 'cashCall',
      label: 'Cash Call',
      type: columnType.amount,
    },
    {
      name: 'freeRidingReq',
      label: 'Free Riding Req',
      type: columnType.amount,
    },
    {
      name: 'freeRidingCall',
      label: 'Free Riding Call',
      type: columnType.amount,
    },
    {
      name: 'overnightBuyingPower',
      label: 'Overnight Buying Power',
      type: columnType.amount,
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    serverSide: true,
  };

  const handleSearchDataChange = (e, checkboxValue) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({
      ...searchData,
      [input.name]: checkboxValue !== undefined ? checkboxValue : input.value,
    });
  };

  const handleSearch = async (paginationChange) => {
    let valid = true;

    if (!searchData.tradeDate) {
      notifyWarning('Trade Date is required.');
      valid = false;
    }

    if (!valid) {
      return;
    }

    try {
      setLoading(true);
      const { fieldsNotZero, ...rest } = searchData;
      QueryParam.set(rest);

      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      localStorage.setItem('search_data', JSON.stringify(searchData));

      const data = await listRequirement(searchData, paginationCopy);

      setTotalRows(data.summary.totalRows);
      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);

      setRows(data.requirementsList);

      setFooterData({
        cashBalance: formatCurrency(data.summary.cashBalance),
        longMarketValue: formatCurrency(data.summary.longMarketValue),
        shortMarketValue: formatCurrency(data.summary.shortMarketValue),
        totalEquity: formatCurrency(data.summary.totalEquity),
        exchangeReq: formatCurrency(data.summary.exchangeReq),
        exchangeCall: formatCurrency(data.summary.exchangeCall),
        fedReq: formatCurrency(data.summary.fedReq),
        fedCall: formatCurrency(data.summary.fedCall),
        houseReq: formatCurrency(data.summary.houseReq),
        houseCall: formatCurrency(data.summary.houseCall),
        pmHouseReq: formatCurrency(data.summary.pmHouseReq),
        pmHouseCall: formatCurrency(data.summary.pmHouseCall),
        timsReq: formatCurrency(data.summary.timsReq),
        timsCall: formatCurrency(data.summary.timsCall),
        buyingPowerCall: formatCurrency(data.summary.buyingPowerCall),
        minBalanceCall: formatCurrency(data.summary.minBalanceCall),
        houseExcess: formatCurrency(data.summary.houseExcess),
        marginEquity: formatCurrency(data.summary.marginEquity),
        fedExcess: formatCurrency(data.summary.fedExcess),
        exchangeExcess: formatCurrency(data.summary.exchangeExcess),
        minBalanceExcess: formatCurrency(data.summary.minBalanceExcess),
        minBalanceReq: formatCurrency(data.summary.minBalanceReq),
        adjFedReq: formatCurrency(data.summary.adjFedReq),
        adjExchangeReq: formatCurrency(data.summary.adjExchangeReq),
        adjHouseReq: formatCurrency(data.summary.adjHouseReq),
        longOptionMarketValue: formatCurrency(data.summary.longOptionMarketValue),
        shortOptionMarketValue: formatCurrency(data.summary.shortOptionMarketValue),
        totalAdjustmentFedReq: formatCurrency(data.summary.totalAdjustmentFedReq),
        totalAdjustmentHouseReq: formatCurrency(data.summary.totalAdjustmentHouseReq),
        totalAdjustmentExchangeReq: formatCurrency(
          data.summary.totalAdjustmentExchangeReq
        ),
        cashCallReq: formatCurrency(data.summary.cashCallReq),
        cashCall: formatCurrency(data.summary.cashCall),
        freeRidingReq: formatCurrency(data.summary.freeRidingReq),
        freeRidingCall: formatCurrency(data.summary.freeRidingCall),
        overnightBuyingPower: formatCurrency(data.summary.overnightBuyingPower),
        pmHouseExcess: formatCurrency(data.summary.pmHouseExcess),
        timsExcess: formatCurrency(data.summary.timsExcess),
      });

      if (paginationChange !== true) {
        notifyInfo(
          data.summary.totalRows > 0
            ? data.summary.totalRows + ' search result(s)'
            : 'No records found.'
        );
      }
    } catch (error) {
      setLoading(false);
      notifyError(error.message);
      console.log(error);
      return;
    } finally {
      setLoading(false);
    }
  };

  const getCsvData = async () => {
    if (totalRows === 0) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };

    const data = await listRequirement(searchData, paginationCopy);
    return data.requirementsList;
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Margin Requirement" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              type="search"
              label={loading ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <DateTimePicker
              type="date"
              name="tradeDate"
              label="Trade Date"
              value={searchData.tradeDate}
              onChange={handleSearchDataChange}
              required={true}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              freeSolo={true}
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              freeSolo={true}
              name="masterAccountNo"
              value={searchData.masterAccountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountName
              freeSolo={true}
              name="accountName"
              value={searchData.accountName}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectMultiple
              label="Fields Not Zero"
              name="fieldsNotZero"
              options={columns
                .filter((col) => {
                  return col.name.includes('Call');
                })
                .map((col) => {
                  return col.label;
                })}
              onChange={handleSearchDataChange}
              value={searchData.fieldsNotZero || []}
            />
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Margin Requirement'}
          data={rows}
          columns={columns}
          options={options}
          pagination={pagination}
          setPagination={setPagination}
          onSearch={handleSearch}
          footerData={footerData}
          getCsvData={getCsvData}
        />
        {isOpen && (
          <MarginRequirementDetails
            onClose={handleClose}
            open={isOpen}
            value={rowData}
          ></MarginRequirementDetails>
        )}
      </div>
    </form>
  );
}
